/**
 * Template Substitution Utility
 * Replaces tokens in message templates with actual values
 * Matching mobile's implementation
 */

interface TemplateTokens {
  name?: string;
  customerName?: string;
  firstName?: string;
  lastName?: string;
  organization?: string;
  user?: string;
  userName?: string;
  project?: string;
  date?: string | Date;
  time?: string | Date;
  timeZone?: string;
}

/**
 * Replace template tokens with actual values
 * Supports case-insensitive token matching
 */
export function substituteTemplate(
  template: string,
  tokens: TemplateTokens
): string {
  if (!template) return '';

  let result = template;

  // Format date (matching mobile app format)
  const formattedDate = tokens.date
    ? (tokens.date instanceof Date ? tokens.date : new Date(tokens.date)).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  // Format time (matching mobile app format)
  const formattedTime = tokens.time
    ? (tokens.time instanceof Date ? tokens.time : new Date(tokens.time)).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    : '';

  // Define token replacements (case-insensitive)
  // If a value is undefined, we don't replace it (keep the placeholder visible)
  const replacements: Array<[RegExp, string | undefined]> = [
    // Name variations
    [/\{name\}/gi, tokens.name || tokens.customerName || tokens.firstName],
    [/\{customerName\}/gi, tokens.customerName || tokens.name],
    [/\{firstName\}/gi, tokens.firstName],
    [/\{lastName\}/gi, tokens.lastName],

    // Organization/Company
    [/\{organization\}/gi, tokens.organization],
    [/\{companyname\}/gi, tokens.organization], // Alias for {organization}

    // User/Agent
    [/\{user\}/gi, tokens.user || tokens.userName],
    [/\{userName\}/gi, tokens.userName || tokens.user],
    [/\{user name\}/gi, tokens.userName || tokens.user],

    // Project
    [/\{project\}/gi, tokens.project],

    // Date/Time
    [/\{date\}/gi, formattedDate || undefined],
    [/\{time\}/gi, formattedTime || undefined],
    [/\{timezone\}/gi, tokens.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone],
  ];

  // Apply all replacements - only replace if we have a value (not undefined)
  for (const [pattern, replacement] of replacements) {
    if (replacement !== undefined && replacement !== null && replacement !== '') {
      result = result.replace(pattern, replacement);
    }
  }

  return result;
}

/**
 * Get preview of template with placeholder values
 */
export function getTemplatePreview(template: string): string {
  return substituteTemplate(template, {
    name: '{Name}',
    organization: '{Organization}',
    user: '{User}',
    project: '{Project}',
    date: '{Date}',
    time: '{Time}',
  });
}

/**
 * Validate template has required tokens
 */
export function validateTemplate(
  template: string,
  requiredTokens: string[]
): { valid: boolean; missing: string[] } {
  const missing: string[] = [];

  for (const token of requiredTokens) {
    const pattern = new RegExp(`\\{${token}\\}`, 'i');
    if (!pattern.test(template)) {
      missing.push(token);
    }
  }

  return {
    valid: missing.length === 0,
    missing,
  };
}

/**
 * Extract all tokens from template
 */
export function extractTokens(template: string): string[] {
  const tokenPattern = /\{([^}]+)\}/g;
  const tokens: string[] = [];
  let match;

  while ((match = tokenPattern.exec(template)) !== null) {
    tokens.push(match[1]);
  }

  return [...new Set(tokens)]; // Remove duplicates
}
